import React, { useEffect, useRef, useState } from "react";
import { Video, Square, RotateCcw, Check } from "lucide-react";

export function VideoRecorder({ onUpload, token, maxSeconds = 60 }) {
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [blobUrl, setBlobUrl] = useState("");
  const [blob, setBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [err, setErr] = useState("");
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const start = async () => {
    try {
      setErr("");
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 720, height: 720 }, audio: true });
      setStream(s);
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.muted = true; }
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
      const rec = new MediaRecorder(s, { mimeType: mime, videoBitsPerSecond: 1_500_000 });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "video/webm" });
        setBlob(b); setBlobUrl(URL.createObjectURL(b));
        s.getTracks().forEach((t) => t.stop());
        setStream(null); clearInterval(tickRef.current);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds((v) => {
        if (v + 1 >= maxSeconds) { stop(); }
        return v + 1;
      }), 1000);
    } catch (e) {
      setErr("Camera access denied. You can still submit a text-only vouch.");
    }
  };

  const stop = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const retake = () => {
    setBlob(null); setBlobUrl(""); setSeconds(0); setUploaded(null);
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const upload = async () => {
    if (!blob) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", blob, "vouch.webm");
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const r = await fetch(`${API}/uploads/public?token=${token}`, { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json())?.detail || "Upload failed");
      const data = await r.json();
      setUploaded(data);
      onUpload && onUpload(data.url);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  };

  useEffect(() => () => { stream?.getTracks().forEach((t) => t.stop()); clearInterval(tickRef.current); }, []);

  const mmss = `${String(Math.floor(seconds/60)).padStart(2,"0")}:${String(seconds%60).padStart(2,"0")}`;

  return (
    <div className="rounded-2xl border-2 border-dashed border-ink/15 bg-parchment-50 p-4">
      <div className="aspect-square max-w-md mx-auto rounded-xl overflow-hidden bg-ink relative">
        {!blobUrl && (
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
        )}
        {blobUrl && (
          <video src={blobUrl} controls className="w-full h-full object-cover" />
        )}
        {recording && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-ochre-300 text-parchment text-xs font-mono px-3 py-1 rounded-full animate-pulse">
            <span className="w-2 h-2 rounded-full bg-parchment" /> REC · {mmss}
          </div>
        )}
        {!stream && !blobUrl && (
          <div className="absolute inset-0 flex items-center justify-center text-parchment/60 text-sm text-center px-6">
            Optional · Record a 60-second vouch on video
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
        {!stream && !blobUrl && (
          <button type="button" data-testid="vr-start" onClick={start}
            className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 text-sm flex items-center gap-2">
            <Video size={14} /> Start recording
          </button>
        )}
        {recording && (
          <button type="button" data-testid="vr-stop" onClick={stop}
            className="btn-pill bg-ochre-300 text-parchment hover:bg-ochre-400 text-sm flex items-center gap-2">
            <Square size={14} /> Stop
          </button>
        )}
        {blobUrl && !uploaded && (
          <>
            <button type="button" data-testid="vr-retake" onClick={retake}
              className="btn-pill bg-white border border-ink/10 text-ink text-sm flex items-center gap-2">
              <RotateCcw size={14} /> Retake
            </button>
            <button type="button" data-testid="vr-upload" onClick={upload} disabled={busy}
              className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 text-sm flex items-center gap-2 disabled:opacity-50">
              <Check size={14} /> {busy ? "Uploading…" : "Save this vouch video"}
            </button>
          </>
        )}
        {uploaded && (
          <div className="text-sm text-ivy font-medium flex items-center gap-2"><Check size={16} /> Video attached</div>
        )}
      </div>

      {err && <div className="text-xs text-ochre-300 mt-2 text-center">{err}</div>}
    </div>
  );
}
